import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import { LoadingButton } from "@mui/lab";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  IUserLoginInfo,
  UserLoginControlState,
} from "./instituteLoginModels";
import { FunctionComponent } from "react";
import { FormType } from "../common/ui-form";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import LoginService from "./loginService";
import { TokenUserHelper } from "../common/services/token-user-service";
import { UIConfigurationContext } from "../common/contexts/ui-configurationContext";
import React from "react";

function Copyright(props: any) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {"Copyright © "}{new Date().getFullYear()}{" "}
      <Link color="inherit" href="http://www.eabhyasacademy.com" target="_blank" rel="noopener">
        eABHYAS ACADEMY
      </Link>
    </Typography>
  );
}

function NeedHelp(props: any) {
  return (
    <Box {...props}>
      <Divider sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">Need Help?</Typography>
      </Divider>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
        <Link
          href="mailto:eabhyasacademy@gmail.com"
          color="text.secondary"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}
        >
          <EmailIcon fontSize="small" /> eabhyasacademy@gmail.com
        </Link>
        <Link
          href="tel:9030779982"
          color="text.secondary"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}
        >
          <PhoneIcon fontSize="small" /> 9030779982
        </Link>
      </Box>
    </Box>
  );
}

const theme = createTheme();

export const LoginFieldSet: FunctionComponent<
  FormType<IUserLoginInfo>
> = ({
  values,
  handleBlur,
  handleChange,
  errors,
  isValid,
  dirty,
  getValidationSchema,
  getControlState,
  isRequired,
  ...props
}) => {
    const navigate = useNavigate();
    const uiConfigData = React.useContext(UIConfigurationContext);
    const [loading, setLoading] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const loginSubmit = async (
      loginRequest: IUserLoginInfo
    ): Promise<void> => {
      setLoading(true);
      await LoginService.login(loginRequest)
        .then((result) => {
          if (result.data.errors && result.data.errors.length > 0) {
            toast.error(
              result.data.errors[0].message
            )
          }
          else if (result.data.data) {
            TokenUserHelper.Save(result.data.data);
            const tokenUser = TokenUserHelper.Get();
            if (tokenUser === undefined) {
              navigate("/user/login");
            }
            else {
              navigate("/home", { replace: true });
            }
          }
        })
        .catch(() =>
          toast.error("Invalid username or password. Please try again.")
        )
        .finally(() => setLoading(false));
    };

    const handleSubmit = () => {
      if (isValid) {
        loginSubmit(values);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSubmit();
    };

    const controlState: UserLoginControlState = getControlState(values);

    return (
      <ThemeProvider theme={theme}>
        <Grid container component="main" sx={{ height: "100vh", width: "100vw", overflow: "hidden" }}>
          <CssBaseline />

          {/* ── Left panel: banner image ── */}
          <Grid
            item
            xs={false}
            sm={4}
            md={7}
            sx={{
              backgroundRepeat: "no-repeat",
              backgroundColor: (t: any) =>
                t.palette.mode === "light" ? t.palette.grey[50] : t.palette.grey[900],
            }}
          >
            <img
              src="https://exams.eabhyasacademy.com/banner/1.jpg"
              alt="Login banner"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </Grid>

          {/* ── Right panel: login form ── */}
          <Grid
            item
            xs={12}
            sm={8}
            md={5}
            component={Paper}
            elevation={0}
            square
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box sx={{ width: '100%', maxWidth: 420, px: 4, py: 6 }}>

              {/* heading */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ m: 1, bgcolor: 'primary.main', width: 48, height: 48 }}>
                  <LockOutlinedIcon />
                </Avatar>
                <Typography component="h1" variant="h5" fontWeight={600}>
                  Welcome back
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Sign in to your account to continue
                </Typography>
              </Box>

              {/* form */}
              <Box component="form" noValidate onKeyDown={handleKeyDown}>
                <TextField
                  id="txtUserName"
                  margin="normal"
                  label={controlState.userName.label.userName}
                  disabled={controlState.userName.disable.userName}
                  onBlur={handleBlur}
                  value={values.userName}
                  onChange={handleChange}
                  name="userName"
                  error={!!errors.userName}
                  helperText={errors.userName}
                  fullWidth
                  autoFocus
                  autoComplete="username"
                />
                <TextField
                  id="txtPassword"
                  margin="normal"
                  label={controlState.passWord.label.passWord}
                  disabled={controlState.passWord.disable.passWord}
                  onBlur={handleBlur}
                  value={values.passWord}
                  onChange={handleChange}
                  name="passWord"
                  error={!!errors.passWord}
                  helperText={errors.passWord}
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <LoadingButton
                  type="button"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mt: 3, mb: 2, py: 1.4, borderRadius: 2, fontWeight: 600, fontSize: '1rem' }}
                  disabled={!isValid}
                  onClick={handleSubmit}
                  loading={loading}
                >
                  Sign In
                </LoadingButton>

                <Copyright sx={{ mt: 3 }} />
                <NeedHelp sx={{ mt: 3 }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </ThemeProvider>
    );
  };
